import { Component, Input, OnInit, OnDestroy, TrackByFunction } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatIconModule } from '@angular/material/icon';
import { AI_SPARKLE_ICON } from '../import/import-analyzing-step/import-analyzing-step.component';

export type AiThinkingType = 'bank-rec' | 'budget-upload' | 'generic';
export type AiThinkingVariant = 'card' | 'simple';

/** Step definition for steps mode */
export interface AiThinkingStep {
    label: string;
    completed: boolean;
}

/**
 * Shared AI Thinking Widget
 *
 * Displays a loading state with animated sparkle icon and rotating messages.
 * Used during AI-powered operations like bank reconciliation parsing or budget generation.
 *
 * ## Modes
 *
 * **Default Mode** - Rotates through messages based on `type`:
 * ```html
 * <app-ai-thinking-widget type="bank-rec"></app-ai-thinking-widget>
 * ```
 *
 * **Custom Title Mode** - Shows static title:
 * ```html
 * <app-ai-thinking-widget
 *     [customTitle]="'Analyzing Sunny Hills HOA'"
 *     [subtitle]="'AI is searching for the best document matches...'">
 * </app-ai-thinking-widget>
 * ```
 *
 * **Steps Mode** - Shows checklist of steps with progress bar:
 * ```html
 * <app-ai-thinking-widget
 *     [customTitle]="'Analyzing Sunny Hills HOA'"
 *     [subtitle]="'AI is searching for the best document matches...'"
 *     [showSteps]="true"
 *     [steps]="analysisSteps"
 *     [showProgress]="true"
 *     [progress]="68">
 * </app-ai-thinking-widget>
 * ```
 */
@Component({
    selector: 'app-ai-thinking-widget',
    templateUrl: './ai-thinking-widget.component.html',
    styleUrls: ['./ai-thinking-widget.component.scss'],
    standalone: true,
    imports: [CommonModule, MatProgressBarModule, MatIconModule],
})
export class AiThinkingWidgetComponent implements OnInit, OnDestroy {
    /** AI sparkle icon path for template binding */
    readonly sparkleIcon = AI_SPARKLE_ICON;

    /** Type of AI operation - determines which messages to show */
    @Input() type: AiThinkingType = 'generic';

    /** Visual variant: 'card' shows large dark icon container, 'simple' shows minimal icon */
    @Input() variant: AiThinkingVariant = 'card';

    /** Optional custom title (overrides rotating messages) */
    @Input() customTitle: string = null;

    /** Subtitle text shown below the rotating title */
    @Input() subtitle: string = 'This may take up to a minute. Thanks for your patience!';

    /** Custom messages array (overrides type-based messages) */
    @Input() customMessages: string[] = null;

    /** Whether to show steps mode (checklist with completion states) */
    @Input() showSteps = false;

    /** Steps to show when in steps mode */
    @Input() steps: AiThinkingStep[] = [];

    /** Whether to show the progress bar */
    @Input() showProgress = false;

    /** Progress percentage (0-100) */
    @Input() progress = 0;

    /** TrackBy function for ngFor performance */
    trackByStep: TrackByFunction<AiThinkingStep> = (_, step) => step.label;

    /** Message arrays for different operation types */
    private readonly messagesByType: Record<AiThinkingType, string[]> = {
        'bank-rec': [
            'Analyzing your bank statement...',
            'Reading through transaction details...',
            'Matching deposits and payments...',
            'Cross-referencing with your ledger...',
            'Almost there, just a few more moments...',
            'Double-checking the numbers...',
            'Organizing everything for you...',
            'Putting the finishing touches on...',
        ],
        'budget-upload': [
            'Reading your uploaded file...',
            'Extracting budget categories...',
            'Analyzing amounts and periods...',
            'Matching to your chart of accounts...',
            'Preparing category suggestions...',
            'Almost ready for your review...',
        ],
        'generic': [
            'Processing your request...',
            'Analyzing data...',
            'Almost there...',
            'Finishing up...',
        ],
    };

    /** Current message index for rotation */
    currentMessageIndex = 0;

    /** Fade state for message transitions */
    messageFadeState: 'visible' | 'fading' = 'visible';

    /** Interval reference for cleanup */
    private messageInterval: ReturnType<typeof setInterval> = null;

    get currentMessage(): string {
        if (this.customTitle) {
            return this.customTitle;
        }
        const messages = this.getMessages();
        return messages[this.currentMessageIndex];
    }

    /** Get the appropriate messages array */
    private getMessages(): string[] {
        if (this.customMessages?.length) {
            return this.customMessages;
        }
        return this.messagesByType[this.type] || this.messagesByType['generic'];
    }

    ngOnInit(): void {
        // Only start rotation if no custom title and not in steps mode
        if (!this.customTitle && !this.showSteps) {
            this.startMessageRotation();
        }
    }

    ngOnDestroy(): void {
        this.stopMessageRotation();
    }

    private startMessageRotation(): void {
        this.currentMessageIndex = 0;
        this.messageFadeState = 'visible';

        const messages = this.getMessages();

        this.messageInterval = setInterval(() => {
            // Start fade out
            this.messageFadeState = 'fading';

            // After fade out completes, change message and fade in
            setTimeout(() => {
                this.currentMessageIndex = (this.currentMessageIndex + 1) % messages.length;
                this.messageFadeState = 'visible';
            }, 300); // Match CSS transition duration
        }, 3000); // Rotate every 3 seconds
    }

    private stopMessageRotation(): void {
        if (this.messageInterval) {
            clearInterval(this.messageInterval);
            this.messageInterval = null;
        }
    }
}
